// patientActivitiesService.js - VERSIÓN COMPLETA MEJORADA

import {
  API_BASE_PATIENT_ACTIVITIES_URL,
  API_PATIENT_ACTIVITIES_ALL_PATIENTS,
  API_BASE_BILLING_URL
} from '../../../helpers/urlHelper';

/**
 * Función auxiliar para realizar peticiones HTTP
 */
const makeHttpRequest = async (url, options = {}) => {
  try {
    const defaultOptions = {
      headers: {
        'Content-Type': 'application/json'
      }
    };

    console.log('HTTP Request:', url, options.method || 'GET');
    
    const response = await fetch(url, { ...defaultOptions, ...options });
    
    if (!response.ok) {
      console.error(`HTTP Error ${response.status}:`, response.statusText);
      
      let errorMessage;
      if (response.status === 400) {
        errorMessage = 'Datos enviados incorrectos';
      } else if (response.status === 404) {
        errorMessage = 'Recurso no encontrado';
      } else if (response.status === 500) {
        errorMessage = 'Error interno del servidor';
      } else {
        errorMessage = `Error HTTP ${response.status}`;
      }
      
      throw new Error(errorMessage);
    }

    const data = await response.json();
    return data;
    
  } catch (error) {
    console.error('HTTP Request Error:', error);
    throw error;
  }
};

/**
 * ✅ FUNCIÓN PRINCIPAL: Obtener actividades de todos los pacientes
 * GET /api/patient-activities/all-patients
 */
export const getAllPatientsActivities = async (params = {}) => {
  try {
    // ✅ URL CORRECTA
    const url = `${API_BASE_PATIENT_ACTIVITIES_URL}${API_PATIENT_ACTIVITIES_ALL_PATIENTS}`;
    // Resulta en: /api/patient-activities/all-patients
    
    console.log('🚀 [getAllPatientsActivities] Iniciando petición');
    console.log('📍 URL final:', url);
    console.log('📊 Parámetros enviados:', params);
    
    // Construir query parameters
    const queryParams = new URLSearchParams();
    
    // ⭐ PARÁMETROS CLAVE PARA FILTRAR SOLO ACTIVIDADES DISPONIBLES
    if (params.only_unbilled !== undefined) {
      queryParams.append('only_unbilled', params.only_unbilled.toString());
    } else {
      queryParams.append('only_unbilled', 'true'); // Por defecto solo no facturadas
    }
    
    if (params.include_existing_af !== undefined) {
      queryParams.append('include_existing_af', params.include_existing_af.toString());
    } else {
      queryParams.append('include_existing_af', 'false'); // Por defecto excluir AF existentes
    }
    
    // Tipos de actividades a incluir
    if (params.include_types && Array.isArray(params.include_types) && params.include_types.length > 0) {
      params.include_types.forEach(type => {
        queryParams.append('include_types', type);
      });
    } else {
      // Por defecto incluir todos los tipos
      const defaultTypes = [
        'appointments', 'prescriptions', 'electronic_prescriptions', 
        'treatments', 'laboratory', 'imaging', 'molecular', 'vaccinations', 'medical_records'
      ];
      defaultTypes.forEach(type => {
        queryParams.append('include_types', type);
      });
    }
    
    // Filtros de fecha
    if (params.date_from) queryParams.append('date_from', params.date_from);
    if (params.date_to) queryParams.append('date_to', params.date_to);
    
    // Paginación
    if (params.limit) queryParams.append('limit', params.limit.toString());
    if (params.offset) queryParams.append('offset', params.offset.toString());
    
    // Búsqueda de pacientes
    if (params.patient_search) queryParams.append('patient_search', params.patient_search);
    
    // Filtros de médico y centro
    if (params.doctor_id) queryParams.append('doctor_id', params.doctor_id);
    if (params.center_id) queryParams.append('center_id', params.center_id);
    
    const finalUrl = queryParams.toString() ? `${url}?${queryParams.toString()}` : url;
    console.log('🌐 URL final con query params:', finalUrl);
    
    const response = await makeHttpRequest(finalUrl, { method: 'GET' });
    
    console.log('📡 Respuesta del servidor:', response);
    
    // Validar estructura de respuesta
    if (!response || typeof response !== 'object') {
      throw new Error('Respuesta inválida del servidor');
    }
    
    if (response.error) {
      console.error('❌ Error en respuesta:', response.error);
      throw new Error(response.error.message || 'Error del servidor');
    }
    
    // Validar que tenemos la estructura esperada
    if (!response.data) {
      throw new Error('La respuesta del servidor no contiene datos');
    }
    
    console.log('✅ Datos recibidos:', {
      patients_count: response.data.patients?.length || 0,
      global_summary: response.data.global_summary,
      pagination: response.data.pagination
    });
    
    return response;
    
  } catch (error) {
    console.error('❌ Error en getAllPatientsActivities:', error);
    throw new Error(`Error al obtener actividades de todos los pacientes: ${error.message}`);
  }
};

/**
 * ✅ Obtener actividades de un paciente específico
 * GET /api/patient-activities/:patientId
 */
export const getPatientActivities = async (patientId, params = {}) => {
  try {
    // Validación básica del patientId
    if (!patientId) {
      throw new Error('patientId es requerido');
    }
    
    // ✅ URL CORRECTA para un paciente específico
    const url = `${API_BASE_PATIENT_ACTIVITIES_URL}/${patientId}`;
    // Resulta en: /api/patient-activities/123456
    
    console.log('🚀 [getPatientActivities] Iniciando petición');
    console.log('👤 Patient ID:', patientId);
    console.log('📍 URL final:', url);
    console.log('📊 Parámetros enviados:', params);
    
    const queryParams = new URLSearchParams();
    
    // Parámetros clave para filtrar
    if (params.only_unbilled !== undefined) {
      queryParams.append('only_unbilled', params.only_unbilled.toString());
    } else {
      queryParams.append('only_unbilled', 'true');
    }
    
    if (params.include_existing_af !== undefined) {
      queryParams.append('include_existing_af', params.include_existing_af.toString());
    } else {
      queryParams.append('include_existing_af', 'false');
    }
    
    // Tipos de actividades a incluir
    if (params.include_types && Array.isArray(params.include_types) && params.include_types.length > 0) {
      params.include_types.forEach(type => {
        queryParams.append('include_types', type);
      });
    }
    
    // Filtros de fecha
    if (params.date_from) queryParams.append('date_from', params.date_from);
    if (params.date_to) queryParams.append('date_to', params.date_to);
    
    const finalUrl = queryParams.toString() ? `${url}?${queryParams.toString()}` : url;
    console.log('🌐 URL final con query params:', finalUrl);
    
    const response = await makeHttpRequest(finalUrl, { method: 'GET' });
    
    console.log('📡 Respuesta del servidor:', response);
    
    // Validar estructura de respuesta
    if (!response || typeof response !== 'object') {
      throw new Error('Respuesta inválida del servidor');
    }
    
    if (response.error) {
      console.error('❌ Error en respuesta:', response.error);
      throw new Error(response.error.message || 'Error del servidor');
    }
    
    return response;
    
  } catch (error) {
    console.error('❌ Error en getPatientActivities:', error);
    throw new Error(`Error al obtener actividades del paciente: ${error.message}`);
  }
};

/**
 * ✅ Crear múltiples actividades facturables de forma masiva
 * POST /api/billing/billable-activities/massive
 */
export const createBillableActivitiesMassive = async (patientId, data) => {
  try {
    // Validación básica
    if (!patientId) {
      throw new Error('patientId es requerido');
    }
    
    // ✅ USAR ENDPOINT DE BILLING PARA CREAR AF
    const url = `${API_BASE_BILLING_URL}/billable-activities/massive`;
    // Resulta en: /api/billing/billable-activities/massive
    
    console.log('🚀 [createBillableActivitiesMassive] Iniciando creación masiva');
    console.log('👤 Patient ID:', patientId);
    console.log('📍 URL:', url);
    console.log('📊 Actividades a crear:', data.selected_activities?.length || 0);
    
    // Validaciones de datos
    if (!data.patient_id && !patientId) {
      throw new Error('patient_id es requerido');
    }
    
    // Asegurar que el patientId esté en los datos
    const requestData = {
      ...data,
      patient_id: data.patient_id || patientId
    };
    
    if (!requestData.selected_activities || !Array.isArray(requestData.selected_activities) || requestData.selected_activities.length === 0) {
      throw new Error('Debe seleccionar al menos una actividad');
    }
    
    console.log('📤 Datos a enviar:', {
      patient_id: requestData.patient_id,
      activities_count: requestData.selected_activities.length,
      doctor_id: requestData.doctor_id,
      center_id: requestData.center_id
    });
    
    const response = await makeHttpRequest(url, {
      method: 'POST',
      body: JSON.stringify(requestData)
    });
    
    console.log('📡 Respuesta del servidor:', response);
    
    // Validar estructura de respuesta
    if (!response || typeof response !== 'object') {
      throw new Error('Respuesta inválida del servidor');
    }
    
    if (response.error) {
      console.error('❌ Error en respuesta:', response.error);
      throw new Error(response.error.message || 'Error del servidor');
    }
    
    if (!response.data) {
      throw new Error('La respuesta del servidor no contiene datos');
    }
    
    console.log('✅ Actividades facturables creadas:', {
      created: response.data.summary?.created || 0,
      failed: response.data.summary?.failed || 0,
      total: response.data.summary?.total || 0
    });
    
    return response;
    
  } catch (error) {
    console.error('❌ Error en createBillableActivitiesMassive:', error);
    throw new Error(`Error al crear actividades facturables: ${error.message}`);
  }
};

/**
 * ✅ Crear actividad facturable individual (wrapper de la función masiva)
 */
export const createBillableActivity = async (patientId, activityData) => {
  try {
    // Validación básica
    if (!patientId) {
      throw new Error('patientId es requerido');
    }
    
    if (!activityData) {
      throw new Error('Datos de la actividad son requeridos');
    }
    
    // Usar la función masiva con una sola actividad
    const data = {
      patient_id: patientId,
      selected_activities: [activityData],
      default_pricing: activityData.pricing || {}
    };
    
    return await createBillableActivitiesMassive(patientId, data);
    
  } catch (error) {
    console.error('❌ Error en createBillableActivity:', error);
    throw new Error(`Error al crear actividad facturable: ${error.message}`);
  }
};

// ==========================================
// CONSTANTES Y ENUMS
// ==========================================

export const ACTIVITY_TYPES = {
  APPOINTMENTS: 'appointments',
  PRESCRIPTIONS: 'prescriptions',
  ELECTRONIC_PRESCRIPTIONS: 'electronic_prescriptions',
  LABORATORY: 'laboratory',
  IMAGING: 'imaging',
  MOLECULAR: 'molecular',
  VACCINATIONS: 'vaccinations',
  TREATMENTS: 'treatments',
  MEDICAL_RECORDS: 'medical_records'
};

export const ACTIVITY_TYPE_LABELS = {
  appointments: 'Consultas/Citas',
  prescriptions: 'Recetas Tradicionales',
  electronic_prescriptions: 'Recetas Electrónicas',
  laboratory: 'Análisis de Laboratorio',
  imaging: 'Diagnóstico por Imagen',
  molecular: 'Medicina Molecular',
  vaccinations: 'Vacunaciones',
  treatments: 'Tratamientos',
  medical_records: 'Registros Médicos'
};

export const TAX_PROFILES = {
  EXENTO: 'EXENTO',
  IVA10: 'IVA10',
  IVA21: 'IVA21'
};

export const BILLING_STATUS = {
  UNBILLED: 'unbilled',
  BILLED: 'billed',
  INVOICED: 'invoiced',
  PAID: 'paid',
  CANCELLED: 'cancelled'
};

// ==========================================
// FUNCIONES DE VALIDACIÓN
// ==========================================

/**
 * Validar datos de actividad antes de crear AF
 */
export const validateActivityData = (activity) => {
  const required = ['element_id', 'source_type', 'description'];
  const hasRequired = required.every(field => 
    activity[field] && activity[field].toString().trim() !== ''
  );
  
  if (!hasRequired) {
    const missing = required.filter(field => 
      !activity[field] || activity[field].toString().trim() === ''
    );
    throw new Error(`Campos requeridos faltantes: ${missing.join(', ')}`);
  }
  
  return true;
};

/**
 * Validar parámetros de búsqueda
 */
export const validateSearchParams = (params) => {
  const errors = [];
  
  // Validar fechas
  if (params.date_from && params.date_to) {
    const dateFrom = new Date(params.date_from);
    const dateTo = new Date(params.date_to);
    
    if (dateFrom > dateTo) {
      errors.push('La fecha desde no puede ser mayor que la fecha hasta');
    }
  }
  
  // Validar tipos de actividades
  if (params.include_types && !Array.isArray(params.include_types)) {
    errors.push('include_types debe ser un array');
  }
  
  // Validar paginación
  if (params.limit && (isNaN(params.limit) || params.limit <= 0)) {
    errors.push('El límite debe ser un número mayor que 0');
  }
  
  if (params.offset && (isNaN(params.offset) || params.offset < 0)) {
    errors.push('El offset debe ser un número mayor o igual a 0');
  }
  
  if (errors.length > 0) {
    throw new Error(`Parámetros inválidos: ${errors.join(', ')}`);
  }
  
  return true;
};

// ==========================================
// FUNCIONES DE FORMATEO Y CÁLCULO
// ==========================================

/**
 * Formatear datos de pricing con valores por defecto
 */
export const formatPricing = (pricing = {}) => {
  const defaultPricing = {
    unit_price: 0,
    qty: 1,
    discount: 0,
    tax_profile: TAX_PROFILES.EXENTO,
    tax_rate: 0,
    notes: ''
  };
  
  return { ...defaultPricing, ...pricing };
};

/**
 * Calcular total de una actividad
 */
export const calculateActivityTotal = (pricing) => {
  const { unit_price = 0, qty = 1, discount = 0, tax_rate = 0 } = pricing;
  const subtotal = (unit_price * qty) - discount;
  const tax = subtotal * (tax_rate / 100);
  return {
    subtotal: parseFloat(subtotal.toFixed(2)),
    tax: parseFloat(tax.toFixed(2)),
    total: parseFloat((subtotal + tax).toFixed(2))
  };
};

/**
 * Formatear fecha para filtros
 */
export const formatDateForApi = (date) => {
  if (!date) return null;
  
  if (typeof date === 'string') {
    return date;
  }
  
  if (date instanceof Date) {
    return date.toISOString().split('T')[0];
  }
  
  return null;
};

// ==========================================
// FUNCIONES DE FILTRADO Y AGRUPACIÓN
// ==========================================

/**
 * Filtrar actividades por estado de facturación
 */
export const filterActivitiesByBillingStatus = (activities, status) => {
  if (!Array.isArray(activities)) return [];
  
  return activities.filter(activity => {
    switch (status) {
      case 'billable':
        return activity.is_billable !== false;
      case 'unbilled':
        return activity.billing_status === 'unbilled' || !activity.billing_status;
      case 'billed':
        return activity.billing_status && activity.billing_status !== 'unbilled';
      case 'invoiced':
        return activity.billing_status === 'invoiced';
      case 'paid':
        return activity.billing_status === 'paid';
      case 'cancelled':
        return activity.billing_status === 'cancelled';
      default:
        return true;
    }
  });
};

/**
 * Filtrar actividades por tipo
 */
export const filterActivitiesByType = (activities, types) => {
  if (!activities || typeof activities !== 'object') return {};
  if (!Array.isArray(types) || types.length === 0) return activities;
  
  const filtered = {};
  types.forEach(type => {
    if (activities[type]) {
      filtered[type] = activities[type];
    }
  });
  
  return filtered;
};

/**
 * Filtrar actividades por rango de fechas
 */
export const filterActivitiesByDateRange = (activities, dateFrom, dateTo) => {
  if (!Array.isArray(activities)) return [];
  
  const from = dateFrom ? new Date(dateFrom) : null;
  const to = dateTo ? new Date(dateTo) : null;
  
  return activities.filter(activity => {
    const activityDate = new Date(activity.date || activity.created_at);
    
    if (from && activityDate < from) return false;
    if (to && activityDate > to) return false;
    
    return true;
  });
};

/**
 * Agrupar actividades por tipo
 */
export const groupActivitiesByType = (activities) => {
  if (!activities || typeof activities !== 'object') return {};
  
  const grouped = {};
  
  Object.entries(activities).forEach(([type, typeActivities]) => {
    if (Array.isArray(typeActivities)) {
      grouped[type] = {
        label: ACTIVITY_TYPE_LABELS[type] || type,
        count: typeActivities.length,
        billable_count: typeActivities.filter(a => a.is_billable !== false).length,
        unbilled_count: typeActivities.filter(a => a.billing_status === 'unbilled' || !a.billing_status).length,
        activities: typeActivities
      };
    }
  });
  
  return grouped;
};

/**
 * Agrupar actividades por fecha
 */
export const groupActivitiesByDate = (activities) => {
  if (!Array.isArray(activities)) return {};
  
  return activities.reduce((grouped, activity) => {
    const date = activity.date || activity.created_at;
    const dateKey = date ? new Date(date).toISOString().split('T')[0] : 'sin_fecha';
    
    if (!grouped[dateKey]) {
      grouped[dateKey] = [];
    }
    
    grouped[dateKey].push(activity);
    return grouped;
  }, {});
};

/**
 * Agrupar actividades por paciente
 */
export const groupActivitiesByPatient = (patientsData) => {
  if (!Array.isArray(patientsData)) return {};
  
  return patientsData.reduce((grouped, patient) => {
    if (patient.patient_id) {
      grouped[patient.patient_id] = {
        patient_info: {
          id: patient.patient_id,
          name: patient.patient_name,
          surname: patient.patient_surname,
          healthcare_id: patient.patient_healthcare_id
        },
        activities: patient.activities || {},
        summary: patient.summary || {}
      };
    }
    return grouped;
  }, {});
};

// ==========================================
// FUNCIONES DE ANÁLISIS Y RESUMEN
// ==========================================

/**
 * Calcular resumen de actividades
 */
export const calculateActivitiesSummary = (activities) => {
  let totalActivities = 0;
  let totalBillable = 0;
  let totalBilled = 0;
  let totalUnbilled = 0;
  
  const byType = {};
  
  if (activities && typeof activities === 'object') {
    Object.entries(activities).forEach(([type, typeActivities]) => {
      if (Array.isArray(typeActivities)) {
        const count = typeActivities.length;
        const billableCount = typeActivities.filter(a => a.is_billable !== false).length;
        const unbilledCount = typeActivities.filter(a => a.billing_status === 'unbilled' || !a.billing_status).length;
        const billedCount = count - unbilledCount;
        
        totalActivities += count;
        totalBillable += billableCount;
        totalBilled += billedCount;
        totalUnbilled += unbilledCount;
        
        byType[type] = {
          total: count,
          billable: billableCount,
          billed: billedCount,
          unbilled: unbilledCount,
          label: ACTIVITY_TYPE_LABELS[type] || type
        };
      }
    });
  }
  
  return {
    total_activities: totalActivities,
    total_billable: totalBillable,
    total_billed: totalBilled,
    total_unbilled: totalUnbilled,
    by_type: byType
  };
};

/**
 * Calcular estadísticas de pacientes
 */
export const calculatePatientsStats = (patientsData) => {
  if (!Array.isArray(patientsData)) {
    return {
      total_patients: 0,
      patients_with_activities: 0,
      patients_with_billable: 0,
      avg_activities_per_patient: 0
    };
  }
  
  const stats = patientsData.reduce((acc, patient) => {
    const summary = patient.summary || {};
    
    acc.total_patients += 1;
    
    if (summary.total_activities > 0) {
      acc.patients_with_activities += 1;
      acc.total_activities += summary.total_activities;
    }
    
    if (summary.total_billable > 0) {
      acc.patients_with_billable += 1;
    }
    
    return acc;
  }, {
    total_patients: 0,
    patients_with_activities: 0,
    patients_with_billable: 0,
    total_activities: 0
  });
  
  stats.avg_activities_per_patient = stats.patients_with_activities > 0 
    ? parseFloat((stats.total_activities / stats.patients_with_activities).toFixed(2))
    : 0;
  
  return stats;
};

// ==========================================
// FUNCIONES DE UTILIDAD PARA UI
// ==========================================

/**
 * Preparar datos para exportación
 */
export const prepareDataForExport = (patientsData, format = 'csv') => {
  if (!Array.isArray(patientsData)) return [];
  
  const flatData = [];
  
  patientsData.forEach(patient => {
    const baseInfo = {
      patient_id: patient.patient_id,
      patient_name: patient.patient_name,
      patient_surname: patient.patient_surname,
      patient_healthcare_id: patient.patient_healthcare_id
    };
    
    if (patient.activities && typeof patient.activities === 'object') {
      Object.entries(patient.activities).forEach(([type, activities]) => {
        if (Array.isArray(activities)) {
          activities.forEach(activity => {
            flatData.push({
              ...baseInfo,
              activity_type: ACTIVITY_TYPE_LABELS[type] || type,
              activity_id: activity.element_id,
              activity_description: activity.description,
              activity_date: activity.date || activity.created_at,
              is_billable: activity.is_billable,
              billing_status: activity.billing_status || 'unbilled',
              unit_price: activity.unit_price || 0,
              quantity: activity.qty || 1
            });
          });
        }
      });
    }
  });
  
  return flatData;
};

/**
 * Obtener colores para tipos de actividades (para gráficos)
 */
export const getActivityTypeColor = (type, opacity = 1) => {
  const colors = {
    appointments: `rgba(59, 130, 246, ${opacity})`,      // azul
    prescriptions: `rgba(34, 197, 94, ${opacity})`,      // verde
    electronic_prescriptions: `rgba(16, 185, 129, ${opacity})`, // verde claro
    laboratory: `rgba(239, 68, 68, ${opacity})`,         // rojo
    imaging: `rgba(245, 101, 101, ${opacity})`,          // rojo claro
    molecular: `rgba(168, 85, 247, ${opacity})`,         // púrpura
    vaccinations: `rgba(249, 115, 22, ${opacity})`,      // naranja
    treatments: `rgba(14, 165, 233, ${opacity})`,        // azul claro
    medical_records: `rgba(107, 114, 128, ${opacity})`   // gris
  };
  
  return colors[type] || `rgba(107, 114, 128, ${opacity})`;
};

/**
 * Generar parámetros de búsqueda por defecto
 */
export const getDefaultSearchParams = (overrides = {}) => {
  return {
    only_unbilled: true,
    include_existing_af: false,
    include_types: Object.values(ACTIVITY_TYPES),
    limit: 50,
    offset: 0,
    ...overrides
  };
};

/**
 * Validar y limpiar datos de actividad para creación de AF
 */
export const sanitizeActivityForBilling = (activity) => {
  const sanitized = {
    element_id: activity.element_id,
    source_type: activity.source_type,
    description: activity.description || 'Sin descripción',
    date: activity.date || activity.created_at,
    pricing: formatPricing(activity.pricing)
  };
  
  // Validar campos requeridos
  validateActivityData(sanitized);
  
  return sanitized;
};